import React, { useEffect, useState } from 'react';
import {
  Alert,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Typography,
  message,
} from 'antd';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { SaveWorkSafetyDocumentDetails } from '../helper/apiHelper';
import type { WorkSafetyDocumentDetails } from '../Types/interfaces';

const { TextArea } = Input;

const BLOOD_GROUPS = [
  '0(I) Rh+',
  '0(I) Rh-',
  'A(II) Rh+',
  'A(II) Rh-',
  'B(III) Rh+',
  'B(III) Rh-',
  'AB(IV) Rh+',
  'AB(IV) Rh-',
];

interface WorkSafetyDocumentFormProps {
  open: boolean;
  studentId: number;
  processId: number | undefined;
  initialValues: WorkSafetyDocumentDetails | null | undefined;
  onClose: () => void;
  onSaved: () => void;
}

const WorkSafetyDocumentForm = ({
  open,
  studentId,
  processId,
  initialValues,
  onClose,
  onSaved,
}: WorkSafetyDocumentFormProps): React.ReactElement => {
  const { t } = useTranslation();
  const [form] = Form.useForm<WorkSafetyDocumentDetails>();
  const [messageApi, contextHolder] = message.useMessage();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (initialValues) {
        form.setFieldsValue(initialValues);
      }
    }
  }, [open, initialValues, form]);

  const handleOk = async (): Promise<void> => {
    if (!processId) {
      return;
    }

    let values: WorkSafetyDocumentDetails;
    try {
      values = await form.validateFields();
    } catch {
      return; // the form shows what is missing
    }

    setSaving(true);
    try {
      await SaveWorkSafetyDocumentDetails(studentId, processId, values);
      messageApi.success(t('workSafetyForm.saveSuccess'));
      onSaved();
      onClose();
    } catch (error) {
      const serverMessage =
        axios.isAxiosError(error) && typeof error.response?.data === 'string'
          ? error.response.data
          : null;
      messageApi.error(serverMessage ?? t('workSafetyForm.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const required = { required: true, message: t('workSafetyForm.required') };

  const routeFields = (prefix: 'outbound' | 'return'): React.ReactElement => (
    <>
      <Form.Item
        name={`${prefix}Route`}
        label={t('workSafetyForm.route')}
        rules={[required]}
      >
        <TextArea
          rows={3}
          maxLength={600}
          showCount
          placeholder={t('workSafetyForm.routePlaceholder')}
        />
      </Form.Item>
      <Form.Item
        name={`${prefix}Transport`}
        label={t('workSafetyForm.transport')}
        rules={[required]}
      >
        <Input
          maxLength={200}
          placeholder={t('workSafetyForm.transportPlaceholder')}
        />
      </Form.Item>
      <Typography.Text>{t('workSafetyForm.travelTime')}</Typography.Text>
      <Row gutter={16} style={{ marginTop: 8 }}>
        <Col xs={12}>
          <Form.Item name={`${prefix}Hours`} rules={[required]}>
            <InputNumber
              min={0}
              max={23}
              precision={0}
              addonAfter={t('workSafetyForm.hours')}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item name={`${prefix}Minutes`} rules={[required]}>
            <InputNumber
              min={0}
              max={59}
              precision={0}
              addonAfter={t('workSafetyForm.minutes')}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  return (
    <Modal
      title={t('workSafetyForm.title')}
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText={t('workSafetyForm.save')}
      cancelText={t('workSafetyForm.cancel')}
      confirmLoading={saving}
      width={720}
      forceRender
    >
      {contextHolder}
      <Typography.Paragraph type="secondary">
        {t('workSafetyForm.description')}
      </Typography.Paragraph>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('workSafetyForm.romanianWarning')}
      />

      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="bloodGroup"
              label={t('workSafetyForm.bloodGroup')}
              rules={[required]}
            >
              <Select
                options={BLOOD_GROUPS.map((group) => ({
                  label: group,
                  value: group,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="birthCounty"
              label={t('workSafetyForm.birthCounty')}
              rules={[required]}
            >
              <Input maxLength={50} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="apartment" label={t('workSafetyForm.apartment')}>
              <Input maxLength={10} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="studies"
              label={t('workSafetyForm.studies')}
              rules={[required]}
            >
              <Input
                maxLength={100}
                placeholder={t('workSafetyForm.studiesPlaceholder')}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="qualification"
              label={t('workSafetyForm.qualification')}
              rules={[required]}
            >
              <Input maxLength={100} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">{t('workSafetyForm.outbound')}</Divider>
        {routeFields('outbound')}

        <Divider orientation="left">{t('workSafetyForm.return')}</Divider>
        {routeFields('return')}
      </Form>
    </Modal>
  );
};

export default WorkSafetyDocumentForm;
